import denrLogo from '@/assets/denr-logo.png';
import SystemAgreementDisplay from '@/components/SystemAgreementDisplay';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { systemAgreement } from '@/data/systemAgreement';
import { cn } from "@/lib/utils";
import { gql, useApolloClient, useMutation } from '@apollo/client';
import { Loader2 } from "lucide-react";
import { useEffect, useState } from 'react';
import { FaEye, FaEyeSlash, FaLeaf } from 'react-icons/fa';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const REGISTER_USER = gql`
   mutation RegisterUser(
      $input: RegisterUserInput!
   ) {
      registerUser(
         input: $input
      ) {
         token
         user {
            id
            username
            firstName
            lastName
            roles
         }
      }
   }
`;

const LOGIN_USER = gql`
   mutation Login($username: String!, $password: String!) {
      login(username: $username, password: $password) {
         token
         user {
         id
         username
         firstName
         lastName
         roles
         }
      }
   }
`;

const LoadingOverlay = ({ message }) => (
   <div className="fixed inset-0 bg-black/20 backdrop-blur-[2px] flex items-center justify-center z-50">
      <div className="flex items-center gap-2 bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full shadow-lg">
         <Loader2 className="h-5 w-5 animate-spin text-green-600" />
         <p className="text-sm font-medium text-gray-800">
            {message}
         </p>
      </div>
   </div>
);

// Temporary function to show system creator - To be removed when system is paid
const getSystemCreatorInfo = () => {
   return {
      show: true, // Set to false to hide the creator info
      // text: "System developed by Mark Gilbert Reginio. ",
      technicalInfo: "This system consists of 137,403 lines of code(93,724 lines after optimization) across 100+ files, developed within a span of 7 months. Originally developed for DENR-PENRO Marinduque, the system's architecture is designed to be potentially be implemented by other DENR offices in the future.",
      agreement: systemAgreement
   };
};

const FOREST_ILLUSTRATION = "https://images.unsplash.com/photo-1542273917363-3b1817f69a2d?auto=format&fit=crop&q=80&w=1000";
const WAVE_PATTERN = "https://images.unsplash.com/photo-1534447677768-be436bb09401?auto=format&fit=crop&q=80&w=1000";
const BACKGROUND_PATTERN = "https://images.unsplash.com/photo-1501854140801-50d01698950b?auto=format&fit=crop&q=80&w=1000";

const UserAuthPage = () => {

   const creatorInfo = getSystemCreatorInfo();
   const [showAgreement, setShowAgreement] = useState(false);
   const [activeTab, setActiveTab] = useState('signin');
   const [firstName, setFirstName] = useState('');
   const [lastName, setLastName] = useState('');
   const [username, setUsername] = useState('');
   const [password, setPassword] = useState('');
   const [confirmPassword, setConfirmPassword] = useState('');
   const [loginUsername, setLoginUsername] = useState('');
   const [loginPassword, setLoginPassword] = useState('');
   const [showPassword, setShowPassword] = useState(false);
   const [passwordError, setPasswordError] = useState('');
   const navigate = useNavigate();
   const [registerUser] = useMutation(REGISTER_USER);
   const [loginUser] = useMutation(LOGIN_USER);
   const client = useApolloClient();
   const [isLoading, setIsLoading] = useState(false);

   useEffect(() => {
      // Force light mode for auth page
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
   }, []);

   useEffect(() => {
      if (firstName && lastName) {
         const formattedFirstName = firstName.trim().toLowerCase().replace(/\s+/g, '_');
         const formattedLastName = lastName.trim().toLowerCase();
         setUsername(`${formattedFirstName}_${formattedLastName}`);
      } else {
         setUsername('');
      }
   }, [firstName, lastName]);

   const validatePassword = (password) => {
      let errors = [];
      if (!/[A-Z]/.test(password)) errors.push("Password must include at least one uppercase letter.");
      if (!/[0-9]/.test(password)) errors.push("Password must include at least one number.");
      if (password.length < 8) errors.push("Password must be at least 8 characters long.");
      setPasswordError(errors.join(' '));
   };

   const handlePasswordChange = (e) => {
      const newPassword = e.target.value;
      setPassword(newPassword);
      validatePassword(newPassword);
   };

   const handleSignup = async (e) => {
      e.preventDefault();
      if (password !== confirmPassword) {
         toast.error('Passwords do not match.');
         return;
      }
      if (passwordError) {
         toast.error('Please correct the password as per requirements.');
         return;
      }
      try {
         setIsLoading(true);
         const { data } = await registerUser({
            variables: {
               input: {
                  firstName,
                  lastName,
                  username,
                  password,
                  email: "", // Add empty email field since it's in the schema
                  role: 'user',
                  userType: 'Client'
               }
            },
         });

         if (data?.registerUser) {
            const { token, user } = data.registerUser;
            localStorage.setItem('token', token);
            localStorage.setItem('user', JSON.stringify(user));
            toast.success('Signup successful!');
            navigate('/home?newUser=true', { replace: true });
         }
      } catch (error) {
         console.error('Signup error:', error);
         toast.error(error.message || 'Signup failed. Please try again.');
      } finally {
         setIsLoading(false);
      }
   };

   const handleLogin = async (e) => {
      e.preventDefault();

      const errors = [];
      if (!loginUsername) errors.push('Username is required');
      if (!loginPassword) errors.push('Password is required');
      if (errors.length > 0) {
         toast.error('Please fill in all required fields');
         return;
      }

      try {
         setIsLoading(true);
         const { data } = await loginUser({
            variables: {
               username: loginUsername,
               password: loginPassword,
            },
         });
         if (data.login) {
            const { token, user } = data.login;

            // Set token in localStorage
            localStorage.removeItem('user');
            localStorage.removeItem('admin');
            localStorage.removeItem('token');
            localStorage.setItem('token', token);
            localStorage.setItem('user', JSON.stringify(user));

            // Check if user has admin roles and set admin data accordingly
            const isAdmin = user.roles.some(role => [
               'Chief_RPS',
               'Chief_TSD',
               'Technical_Staff',
               'Forest_Ranger',
               'Receiving_Clerk',
               'Releasing_Clerk',
               'Accountant',
               'OOP_Staff_Incharge',
               'Bill_Collector',
               'Credit_Officer',
               'PENR_CENR_Officer',
               'Deputy_CENR_Officer',
               'Inspection_Team',
               'superadmin'
            ].includes(role));

            if (isAdmin) {
               // Create admin object with necessary data
               const adminData = {
                  id: user.id,
                  username: user.username,
                  firstName: user.firstName,
                  lastName: user.lastName,
                  roles: user.roles,
                  themePreference: user.themePreference || 'light'
               };
               // console.log('Admin data before saving:', adminData);
               localStorage.setItem('admin', JSON.stringify(adminData));
               // console.log('Admin data after saving:', JSON.parse(localStorage.getItem('admin')));
            }

            // Initialize theme based on user preferences
            try {
               // Query for user/admin theme preference
               const themeQuery = isAdmin
                  ? await client.query({
                     query: gql`
                        query GetCurrentAdmin {
                           getCurrentAdmin {
                              themePreference
                           }
                        }
                     `
                  })
                  : await client.query({
                     query: gql`
                        query GetCurrentUser {
                           getCurrentUser {
                              themePreference
                           }
                        }
                     `
                  });

               const themePreference = isAdmin
                  ? themeQuery.data.getCurrentAdmin?.themePreference
                  : themeQuery.data.getCurrentUser?.themePreference;

               if (themePreference) {
                  localStorage.setItem('theme', themePreference);
                  if (themePreference === 'dark') {
                     document.documentElement.classList.add('dark');
                  } else {
                     document.documentElement.classList.remove('dark');
                  }
               }
            } catch (themeError) {
               console.error('Error setting theme:', themeError);
            }

            // Force Apollo Client to reset its store
            await client.resetStore();

            toast.success('Login successful!!!');

            if (user.roles.includes('superadmin')) {
               navigate('/superadmin/dashboard', { replace: true });
            } else if (user.roles.some(role => [
               'Chief_RPS',
               'Chief_TSD',
               'Technical_Staff',
               'Forest_Ranger',
               'Receiving_Clerk',
               'Releasing_Clerk',
               'Accountant',
               'OOP_Staff_Incharge',
               'Bill_Collector',
               'Credit_Officer',
               'PENR_CENR_Officer',
               'Deputy_CENR_Officer',
               'Inspection_Team'
            ].includes(role))) {
               navigate('/personnel/home', { replace: true });
            } else if (user.roles.includes('user')) {
               navigate('/home', { replace: true });
            } else {
               toast.error('Unknown user role');
               localStorage.removeItem('token');
               localStorage.removeItem('user');
               localStorage.removeItem('admin');
            }
         }
      } catch (error) {
         console.error('Login error:', error);
         toast.error('Login failed: Invalid credentials');
      } finally {
         setIsLoading(false);
      }
   };

   const handleKeyPress = (e, formType) => {
      if (e.key === 'Enter') {
         e.preventDefault();
         if (formType === 'signin') {
            handleLogin(e);
         } else if (formType === 'signup') {
            handleSignup(e);
         }
      }
   };

   return (
      <div className="min-h-screen relative flex flex-col">
         {/* Full-screen background image with overlay */}
         <div
            className="absolute inset-0 z-0"
            style={{
               backgroundImage: `url(${FOREST_ILLUSTRATION})`,
               backgroundSize: 'cover',
               backgroundPosition: 'center',
               backgroundRepeat: 'no-repeat'
            }}
         >
            {/* Dark overlay for better contrast */}
            <div className="absolute inset-0 bg-black/40 backdrop-blur-[3px]" />
         </div>

         {/* Loading Overlay */}
         {isLoading && (
            <LoadingOverlay
               message={activeTab === 'signin' ? 'Signing in...' : 'Creating account...'}
            />
         )}

         {/* Header */}
         <header className="relative z-10">
            <div className="container mx-auto px-4 sm:px-6">
               <div className="flex items-center h-16">
                  <Link
                     className="flex items-center justify-center group hover:scale-105 transition-transform"
                     to="/"
                  >
                     <FaLeaf className="h-5 w-5 sm:h-6 sm:w-6 text-white group-hover:text-green-200" />
                     <span className="ml-2 text-lg sm:text-xl font-bold text-white">
                        PermitTree
                     </span>
                  </Link>
               </div>
            </div>
         </header>

         {/* Main Content */}
         <main className="flex-1 flex items-center justify-center px-4 sm:px-6 py-10 relative z-10">
            <div className="w-full max-w-[420px]">
               {/* DENR Logo and Title Section */}
               <div className="flex flex-col items-center mb-5">
                  <div className="mb-4">
                     <img
                        src={denrLogo}
                        alt="DENR Logo"
                        className="h-36 w-36 object-contain"
                     />
                  </div>
                  <h1 className="font-bold text-white text-xl mb-1 text-center">Department of Environment and Natural Resources</h1>
                  <p className="text-white">PENRO Marinduque</p>
               </div>

               {/* Auth Form Container */}
               <div className="w-full p-6 sm:p-8 bg-white/95 backdrop-blur-md rounded-2xl shadow-2xl">
                  <div className="text-center">
                     {/* <h2 className="text-2xl font-bold text-gray-800 mb-2">
                        Welcome to PermitTree
                     </h2>
                     <p className="text-sm text-gray-600">
                        Your gateway to forest permit management
                     </p> */}
                  </div>

                  <div className="tabs-container w-full">
                     <div
                        className="tabs-list-container bg-gray-100/80 p-1 rounded-xl flex justify-between mb-8"
                        role="tablist"
                        aria-label="Authentication tabs"
                     >
                        <button
                           role="tab"
                           aria-selected={activeTab === 'signin'}
                           aria-controls="signin-panel"
                           id="signin-tab"
                           className={`tabs-trigger py-1 px-6 transition-all duration-200 flex-1 rounded-lg text-sm sm:text-base font-medium
                           ${activeTab === 'signin'
                                 ? 'bg-white text-green-700 shadow-md'
                                 : 'text-gray-600 hover:text-gray-800 hover:bg-white/50'}`}
                           onClick={() => setActiveTab('signin')}
                        >
                           Sign In
                        </button>
                        <button
                           role="tab"
                           aria-selected={activeTab === 'signup'}
                           aria-controls="signup-panel"
                           id="signup-tab"
                           className={`tabs-trigger py-1 px-6 transition-all duration-200 flex-1 rounded-lg text-sm sm:text-base font-medium
                           ${activeTab === 'signup'
                                 ? 'bg-white text-green-700 shadow-md'
                                 : 'text-gray-600 hover:text-gray-800 hover:bg-white/50'}`}
                           onClick={() => setActiveTab('signup')}
                        >
                           Sign Up
                        </button>
                     </div>

                     <div className="tabs-content">
                        <div
                           role="tabpanel"
                           id="signin-panel"
                           aria-labelledby="signin-tab"
                           className="space-y-4"
                           style={{ display: activeTab === 'signin' ? 'block' : 'none' }}
                        >
                           <div>
                              <Label htmlFor="loginUsername" className="text-sm font-medium text-gray-700">
                                 Username
                              </Label>
                              <Input
                                 id="loginUsername"
                                 data-testid="login-username"
                                 type="text"
                                 value={loginUsername}
                                 onChange={(e) => setLoginUsername(e.target.value)}
                                 className="mt-1 w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                                 placeholder="Enter your username"
                                 onKeyPress={(e) => handleKeyPress(e, 'signin')}
                                 autoComplete="username"
                              />
                           </div>
                           <div>
                              <Label htmlFor="loginPassword" className="text-sm font-medium text-gray-700">
                                 Password
                              </Label>
                              <div className="relative">
                                 <Input
                                    id="loginPassword"
                                    data-testid="login-password"
                                    type={showPassword ? "text" : "password"}
                                    value={loginPassword}
                                    onChange={(e) => setLoginPassword(e.target.value)}
                                    className="mt-1 w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                                    placeholder="Enter your password"
                                    onKeyPress={(e) => handleKeyPress(e, 'signin')}
                                    autoComplete="current-password"
                                 />
                                 <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 p-1 rounded-full hover:bg-gray-100 transition-colors"
                                 >
                                    {showPassword ? <FaEyeSlash className="w-4 h-4" /> : <FaEye className="w-4 h-4" />}
                                 </button>
                              </div>
                           </div>
                           <button
                              onClick={handleLogin}
                              data-testid="signin-submit"
                              disabled={isLoading}
                              className={cn(
                                 "w-full bg-gradient-to-r from-green-600 to-green-500 text-white py-2.5 px-4 rounded-lg font-medium",
                                 "hover:from-green-700 hover:to-green-600 focus:ring-2 focus:ring-green-500 focus:ring-offset-2",
                                 "transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98]",
                                 "flex items-center justify-center",
                                 isLoading && "opacity-70 cursor-not-allowed"
                              )}
                           >
                              {isLoading ? (
                                 <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Signing in...
                                 </>
                              ) : (
                                 'Sign In'
                              )}
                           </button>
                           <p className="mt-4 text-sm text-gray-600 text-center">
                              Don't have an account yet?{' '}
                              <button
                                 onClick={() => setActiveTab('signup')}
                                 className="text-green-600 hover:text-green-700 underline font-medium"
                              >
                                 Create Account
                              </button>
                           </p>
                        </div>
                        {/* {creatorInfo.show && (
                           <div className="text-center text-gray-600 mt-8">
                              <span>{creatorInfo.text}</span>
                              <span className="mx-2">|</span>
                              <button
                                 onClick={() => setShowAgreement(true)}
                                 className="text-green-600 hover:text-green-700 underline font-medium"
                              >
                                 View System Preliminary Agreement
                              </button>
                           </div>
                        )} */}
                        {/* Agreement Dialog */}
                        <Dialog open={showAgreement} onOpenChange={setShowAgreement}>
                           <DialogContent className="max-w-5xl max-h-[80vh]">
                              <DialogHeader>
                                 <DialogTitle>System Development and Transfer Agreement</DialogTitle>
                              </DialogHeader>
                              <div className="mt-4 overflow-y-auto pr-4 max-h-[70vh] scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 hover:scrollbar-thumb-gray-400">
                                 <SystemAgreementDisplay />
                              </div>
                           </DialogContent>
                        </Dialog>
                        <div
                           role="tabpanel"
                           id="signup-panel"
                           aria-labelledby="signup-tab"
                           className="space-y-4"
                           style={{ display: activeTab === 'signup' ? 'block' : 'none' }}
                        >
                           <div className="grid grid-cols-2 gap-4">
                              <div>
                                 <Label htmlFor="firstName" className="text-sm font-medium text-gray-700">
                                    First Name
                                 </Label>
                                 <Input
                                    id="firstName"
                                    data-testid="first-name"
                                    type="text"
                                    value={firstName}
                                    onChange={(e) => setFirstName(e.target.value)}
                                    className="mt-1 w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                                    placeholder="First name"
                                    autoComplete="given-name"
                                 />
                              </div>
                              <div>
                                 <Label htmlFor="lastName" className="text-sm font-medium text-gray-700">
                                    Last Name
                                 </Label>
                                 <Input
                                    id="lastName"
                                    data-testid="last-name"
                                    type="text"
                                    value={lastName}
                                    onChange={(e) => setLastName(e.target.value)}
                                    className="mt-1 w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                                    placeholder="Last name"
                                    autoComplete="family-name"
                                 />
                              </div>
                           </div>
                           <div>
                              <Label htmlFor="username" className="text-sm font-medium text-gray-700">
                                 Username
                              </Label>
                              <Input
                                 id="username"
                                 data-testid="username"
                                 type="text"
                                 value={username}
                                 readOnly
                                 className="mt-1 w-full px-4 py-2.5 rounded-lg bg-gray-50 border border-gray-300 focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                                 placeholder="Username will be generated"
                                 autoComplete="username"
                              />
                           </div>
                           <div>
                              <Label htmlFor="password" className="text-sm font-medium text-gray-700">
                                 Password
                              </Label>
                              <div className="relative">
                                 <Input
                                    id="password"
                                    data-testid="signup-password"
                                    type={showPassword ? "text" : "password"}
                                    value={password}
                                    onChange={handlePasswordChange}
                                    className="mt-1 w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                                    placeholder="Create a password"
                                    autoComplete="new-password"
                                 />
                                 <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 p-1 rounded-full hover:bg-gray-100 transition-colors"
                                 >
                                    {showPassword ? <FaEyeSlash className="w-4 h-4" /> : <FaEye className="w-4 h-4" />}
                                 </button>
                              </div>
                              {passwordError && (
                                 <p className="mt-2 text-sm text-red-600">
                                    {passwordError}
                                 </p>
                              )}
                           </div>
                           <div>
                              <Label htmlFor="confirmPassword" className="text-sm font-medium text-gray-700">
                                 Confirm Password
                              </Label>
                              <Input
                                 id="confirmPassword"
                                 data-testid="confirm-password"
                                 type={showPassword ? "text" : "password"}
                                 value={confirmPassword}
                                 onChange={(e) => setConfirmPassword(e.target.value)}
                                 className="mt-1 w-full px-4 py-2.5 rounded-lg border border-gray-300 focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all"
                                 placeholder="Confirm your password"
                                 onKeyPress={(e) => handleKeyPress(e, 'signup')}
                                 autoComplete="new-password"
                              />
                           </div>
                           <button
                              onClick={handleSignup}
                              data-testid="signup-submit"
                              disabled={isLoading}
                              className={cn(
                                 "w-full bg-gradient-to-r from-green-600 to-green-500 text-white py-2.5 px-4 rounded-lg font-medium",
                                 "hover:from-green-700 hover:to-green-600 focus:ring-2 focus:ring-green-500 focus:ring-offset-2",
                                 "transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98]",
                                 "flex items-center justify-center",
                                 isLoading && "opacity-70 cursor-not-allowed"
                              )}
                           >
                              {isLoading ? (
                                 <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Creating Account...
                                 </>
                              ) : (
                                 'Sign Up'
                              )}
                           </button>
                           <p className="mt-4 text-sm text-gray-600 text-center">
                              By signing up, you agree to our{' '}
                              <Link to="/terms" className="text-green-600 hover:text-green-700 underline">
                                 Terms of Service
                              </Link>{' '}
                              and{' '}
                              <Link to="/privacy-policy" className="text-green-600 hover:text-green-700 underline">
                                 Privacy Policy
                              </Link>
                           </p>
                        </div>
                     </div>
                  </div>
               </div>

               {/* Inspirational text below form */}
               {/* <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3, duration: 0.5 }}
                  className="mt-6 text-center text-white text-sm"
               >
                  Join us in protecting and managing our forests sustainably
               </motion.p> */}
            </div>
         </main>

         {/* Footer */}
         <footer className="relative z-10 py-4 sm:py-6 text-center">
            <p className="text-xs sm:text-sm text-white/80">&copy; 2025 DENR-PENRO. All rights reserved.</p>
         </footer>
      </div>
   );
};

export default UserAuthPage;
